import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Settings, FileText, Upload, Download } from "lucide-react";

const FIELDS = [
  { k: "site_name", l: "Site name", type: "input" },
  { k: "tagline", l: "Tagline", type: "input" },
  { k: "hero_title", l: "Homepage hero title", type: "input" },
  { k: "hero_subtitle", l: "Homepage hero subtitle", type: "textarea" },
  { k: "about", l: "About the publisher", type: "textarea" },
  { k: "contact_email", l: "Contact email", type: "input" },
  { k: "contact_title", l: "Contact page — heading", type: "input" },
  { k: "contact_subtitle", l: "Contact page — subtitle", type: "textarea" },
  { k: "contact_address", l: "Contact page — address (one line per row)", type: "textarea" },
  { k: "contact_response_time", l: "Contact page — response time", type: "input" },
  { k: "contact_note", l: "Contact page — sidebar note", type: "textarea" },
  { k: "contact_phone", l: "Contact phone number", type: "input" },
  { k: "contact_map_query", l: "Map location (address or place to show on the map)", type: "input" },
  { k: "social_twitter", l: "Twitter / X profile URL", type: "input" },
  { k: "social_linkedin", l: "LinkedIn profile URL", type: "input" },
  { k: "social_facebook", l: "Facebook page URL", type: "input" },
  { k: "footer_text", l: "Footer text", type: "input" },
];

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data || {})); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("Settings saved. Changes are live on the site."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const uploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["docx", "doc"].includes(ext)) { toast.error("Please choose a .doc or .docx file."); return; }
    setTplBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/manuscript-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, manuscript_template_name: data.filename, manuscript_template_updated_at: new Date().toISOString() }));
      toast.success("Manuscript template updated. It's live on the For Authors page.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setTplBusy(false); }
  };

  if (!s) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div data-testid="settings-admin" className="max-w-2xl">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2"><Settings className="w-6 h-6 text-amber-600" /> Site Settings</h1>
      <p className="text-slate-500 text-sm mb-6">Edit the branding and homepage content shown to visitors.</p>

      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <Label>{f.l}</Label>
            {f.type === "textarea" ? (
              <Textarea value={s[f.k] || ""} onChange={(e) => setS({ ...s, [f.k]: e.target.value })} rows={3} className="mt-1.5" data-testid={`settings-${f.k}`} />
            ) : (
              <Input value={s[f.k] || ""} onChange={(e) => setS({ ...s, [f.k]: e.target.value })} className="mt-1.5" data-testid={`settings-${f.k}`} />
            )}
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-800 p-4">
          <div>
            <Label className="text-sm">Show impact factor on journals</Label>
            <p className="text-xs text-slate-500 mt-0.5">When off, the impact-factor badge is hidden on every journal card and page.</p>
          </div>
          <Switch
            checked={s.show_impact_factor !== false}
            onCheckedChange={(v) => setS({ ...s, show_impact_factor: v })}
            data-testid="settings-show-impact-factor"
          />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-800 p-4">
          <div>
            <Label className="text-sm">Auto-acknowledge contact messages</Label>
            <p className="text-xs text-slate-500 mt-0.5">When on, anyone who submits the Contact form instantly gets a "we've received your message" email.</p>
          </div>
          <Switch
            checked={s.contact_auto_ack !== false}
            onCheckedChange={(v) => setS({ ...s, contact_auto_ack: v })}
            data-testid="settings-contact-auto-ack"
          />
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mt-6" data-testid="settings-manuscript-template">
        <h2 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100 flex items-center gap-2"><FileText className="w-5 h-5 text-amber-600" /> Manuscript Template (Word)</h2>
        <p className="text-slate-500 text-sm mt-1">Replace the downloadable manuscript template shown on the For Authors page. Accepts .doc or .docx (max 25MB).</p>
        <p className="text-xs text-slate-500 mt-3">
          Current file: <span className="font-medium text-slate-700 dark:text-slate-300">{s.manuscript_template_name || "Default Noventra template"}</span>
          {s.manuscript_template_updated_at && <span className="text-slate-400"> · updated {new Date(s.manuscript_template_updated_at).toLocaleString()}</span>}
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button asChild variant="outline" className="rounded-full gap-2" data-testid="settings-download-template">
            <a href={`${BACKEND}/api/manuscript-template`} target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4" /> Download current</a>
          </Button>
          <Button asChild disabled={tplBusy} className="rounded-full gap-2" data-testid="settings-upload-template-btn">
            <label className="cursor-pointer">
              {tplBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload new template
              <input type="file" accept=".doc,.docx" className="hidden" onChange={uploadTemplate} data-testid="settings-upload-template-input" />
            </label>
          </Button>
        </div>
      </div>
    </div>
  );
}
